import type { ColumnDef } from "@tanstack/react-table";
import { DateTime } from "luxon";
import Tag from "@/components/Tag";
import { calculateMonthsBetween, toFormat } from "@/lib/dateHelpers";
import { LEVEL_CODE_TO_VERSION, PGM_VERSION_NAMES } from "../../_shared/constants";
import type { StudentInternshipsQuery } from "../internships/types";
import type { UniversityStudentsQuery } from "../students/types";

export type ProgramLevelEntry = {
  level: string;
  levelCode: string;
  startDate: string;
  endDate: string | null;
  completed: boolean;
  isCurrent: boolean;
};

export type UniversityReportRow = {
  id: string;
  studentId: string;
  lastName: string;
  firstName: string;
  enrollment: string;
  currentLevelCode: string;
  programLevels: ProgramLevelEntry[];
  patAttempts12Months: number | "N/A";
  pat: string;
  tournamentRequirements: string;
  monthsEmployed: number | string;
  currentCohort: string;
  originalCohort: string;
  inOriginalCohort: string;
  universityComments: string;
};

type InternshipByPgaId = Record<string, number>;
export type TournamentProbationByPersonId = Record<string, boolean>;

export type InternshipReportingRow = {
  id: string;
  facilityType: string;
  internshipCount: number;
  studentCount: number;
  totalInternshipCredits: number;
  averageInternshipCredits: number;
};

export type InternshipReportingSummary = {
  internshipCount: number;
  studentCount: number;
  totalInternshipCredits: number;
};

export const SEMESTER_REPORT_CSV_HEADERS = [
  "Last",
  "First",
  "ID",
  "Enrollment",
  "Program Levels",
  "PAT 12 mo",
  "PAT",
  "Tourn Req",
  "Intern",
  "Current Cohort",
  "Original Cohort",
  "In Orig Cohort",
  "Comments"
] as const;

export function semesterReportCsvFilename(date = new Date()): string {
  return `semester-report-${date.toISOString().slice(0, 10)}.csv`;
}

const FACILITY_TYPE_LABELS: Record<string, string> = {
  GOLF_ACADEMY_SCHOOL: "Golf Academy/School",
  GOLF_COMPANY: "Golf Company",
  GOLF_COURSE_PRIVATE: "Golf Course - Private",
  GOLF_COURSE_PUBLIC: "Golf Course - Public",
  GOLF_RANGE: "Golf Range",
  INDOOR_FACILITY: "Indoor Facility",
  ORGANIZATION_ASSOCIATION_ALLIED_BODY: "Organization/Association/Allied Body",
  PGM_UNIVERSITY: "PGM University",
  RESORT: "Resort",
  RETAIL_STORE: "Retail Store",
  OTHER: "Other"
};

function formatDateForReport(date?: string | null): string {
  if (!date) return "";
  const parsed = DateTime.fromISO(date);
  if (!parsed.isValid) return "";
  return parsed.toFormat("MM/dd/yyyy");
}

function formatLevelDateRange(startDate?: string | null, endDate?: string | null): string {
  const formattedStart = startDate ? toFormat(startDate, "MMM d, yyyy") : "";
  const formattedEnd = endDate ? toFormat(endDate, "MMM d, yyyy") : "Present";

  if (!formattedStart) return "";
  return `${formattedStart} – ${formattedEnd}`;
}

export function formatProgramLevelsForExport(levels: ProgramLevelEntry[]): string {
  return levels
    .map((level) => {
      const range = formatLevelDateRange(level.startDate, level.endDate);
      return range ? `Level ${level.level} (${range})` : `Level ${level.level}`;
    })
    .join("; ");
}

export function buildSemesterReportCsvRows(rows: UniversityReportRow[]): string[][] {
  return [
    [...SEMESTER_REPORT_CSV_HEADERS],
    ...rows.map((row) => [
      row.lastName,
      row.firstName,
      row.studentId,
      row.enrollment,
      formatProgramLevelsForExport(row.programLevels),
      row.patAttempts12Months === "N/A" ? "" : String(row.patAttempts12Months),
      row.pat,
      row.tournamentRequirements,
      String(row.monthsEmployed),
      row.currentCohort,
      row.originalCohort,
      row.inOriginalCohort,
      row.universityComments
    ])
  ];
}

function ReportBooleanCell({ value }: { value: string }) {
  if (!value) return <span className="opacity-20">--</span>;

  return (
    <Tag tagType={value === "Yes" ? "success" : "error"} size="small">
      {value}
    </Tag>
  );
}

function ProgramLevelsCell({ levels }: { levels: ProgramLevelEntry[] }) {
  if (!levels.length) {
    return <span className="opacity-20">--</span>;
  }

  return (
    <div className="flex flex-col gap-1 py-0.5">
      {levels.map((level) => (
        <div key={`${level.levelCode}-${level.startDate}`} className="flex items-center gap-2 text-body-small">
          <Tag tagType={level.isCurrent ? "information" : "surface"} size="small">
            Level {level.level}
          </Tag>
          <span className="text-sys-brand-on-surface-variant">
            {formatLevelDateRange(level.startDate, level.endDate)}
          </span>
        </div>
      ))}
    </div>
  );
}

function isPatPassed(status?: string | null): boolean {
  return status?.toUpperCase() === "PASSED";
}

export function getPatAttemptsInLast12Months(
  submissions: { date: string }[] | undefined,
  now: DateTime = DateTime.now()
): number | "N/A" {
  if (!submissions?.length) return "N/A";

  const cutoff = now.minus({ months: 12 }).startOf("day");
  return submissions.filter((submission) => {
    const submissionDate = DateTime.fromISO(submission.date);
    return submissionDate.isValid && submissionDate >= cutoff;
  }).length;
}

function getEstimatedEnrollmentDate(
  history: { startDate: string }[] | undefined,
  latestStartDate?: string | null
): string {
  const validStartDates = (history ?? [])
    .map((item) => DateTime.fromISO(item.startDate))
    .filter((date) => date.isValid)
    .sort((a, b) => a.toMillis() - b.toMillis());

  if (validStartDates.length > 0) {
    return validStartDates[0].toFormat("MM/dd/yyyy");
  }

  return formatDateForReport(latestStartDate);
}

export function mapProgramLevels(
  history:
    | { levelCode: string; level: string; startDate: string; endDate?: string | null; completed: boolean }[]
    | undefined,
  currentLevelCode?: string | null
): ProgramLevelEntry[] {
  if (!history?.length) return [];

  const sorted = [...history].sort((a, b) => {
    const levelComparison = Number(a.level) - Number(b.level);
    if (levelComparison !== 0) return levelComparison;

    return DateTime.fromISO(a.startDate).toMillis() - DateTime.fromISO(b.startDate).toMillis();
  });

  return sorted.map((level, index) => ({
    level: level.level,
    levelCode: level.levelCode,
    startDate: level.startDate,
    endDate: level.endDate || null,
    completed: level.completed,
    isCurrent:
      (!!currentLevelCode && level.levelCode === currentLevelCode) || (index === sorted.length - 1 && !level.endDate)
  }));
}

export function buildTournamentProbationByPersonId(
  probations: Array<{ person: { id: string }; active: boolean; probationType?: string | null }>
): TournamentProbationByPersonId {
  return probations.reduce<TournamentProbationByPersonId>((acc, probation) => {
    if (!probation.active || probation.probationType !== "TOURNAMENT_REQUIREMENTS") {
      return acc;
    }

    acc[probation.person.id] = true;
    return acc;
  }, {});
}

export function getTournamentRequirementsStatus(hasTournamentProbation: boolean): "Yes" | "No" {
  return hasTournamentProbation ? "No" : "Yes";
}

export function formatReportInternshipMonths(value: number | string): string {
  if (value === "" || value === "N/A") return String(value);

  const numeric = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(numeric)) return String(value);

  return roundToTenth(numeric).toFixed(1);
}

export function formatInOriginalCohort(value?: boolean | null): "Yes" | "No" | "" {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "";
}

export function mapUniversityReportRows(
  students: UniversityStudentsQuery["universityStudents"],
  internshipMonthsByPgaId: InternshipByPgaId = {},
  tournamentProbationByPersonId: TournamentProbationByPersonId = {},
  now: DateTime = DateTime.now(),
  canResolveTournamentRequirements = true
): UniversityReportRow[] {
  if (!students?.nodes) return [];

  return students.nodes.map((node) => {
    const internshipFromMap = internshipMonthsByPgaId[node.pgaId];
    const rawInternshipMonths = internshipFromMap ?? node.totalInternshipMonths ?? ("" as const);
    const currentLevelCode = node.educationLatestPgmProgram?.levelCode || "";
    const programLevels = mapProgramLevels(node.educationPgmProgramHistory, currentLevelCode);

    return {
      id: node.pgaId,
      studentId: node.pgaId,
      lastName: node.lastName || "",
      firstName: node.firstName || "",
      enrollment: getEstimatedEnrollmentDate(
        node.educationPgmProgramHistory,
        node.educationLatestPgmProgram?.startDate
      ),
      currentLevelCode,
      programLevels,
      patAttempts12Months: getPatAttemptsInLast12Months(
        node.prospectProgram?.playingAbilityTestStatus?.submissions,
        now
      ),
      pat: isPatPassed(node.prospectProgram?.playingAbilityTestStatus?.status) ? "Yes" : "No",
      tournamentRequirements: canResolveTournamentRequirements
        ? getTournamentRequirementsStatus(!!tournamentProbationByPersonId[node.id])
        : "",
      monthsEmployed:
        typeof rawInternshipMonths === "number"
          ? formatReportInternshipMonths(rawInternshipMonths)
          : rawInternshipMonths,
      currentCohort: node.universityCohortContext?.currentCohort?.name || "",
      originalCohort: node.universityCohortContext?.originalCohort?.name || "",
      inOriginalCohort: formatInOriginalCohort(node.universityCohortContext?.inOriginalCohort),
      universityComments: ""
    };
  });
}

export function buildInternshipMonthsByPgaId(
  internships: StudentInternshipsQuery | undefined,
  now: Date = new Date()
): InternshipByPgaId {
  const edges = internships?.studentInternships?.edges ?? [];
  if (!edges.length) return {};

  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  return edges.reduce<InternshipByPgaId>((acc, edge) => {
    const internship = edge.node;
    const pgaId = internship.student?.pgaId;
    if (!pgaId) return acc;

    const endDate = internship.endedOn ? new Date(internship.endedOn) : null;
    if (endDate) {
      endDate.setHours(0, 0, 0, 0);
    }

    const isInProgress = internship.isActive && internship.startedOn && (!endDate || endDate > today);
    const credits = isInProgress
      ? calculateMonthsBetween(internship.startedOn, today)
      : Number(internship.internshipCredits ?? 0);

    acc[pgaId] = (acc[pgaId] ?? 0) + credits;
    return acc;
  }, {});
}

function roundToTenth(value: number): number {
  return Math.round(value * 10) / 10;
}

function getInternshipCredits(
  internship: StudentInternshipsQuery["studentInternships"]["edges"][number]["node"],
  now: Date
): number {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const endDate = internship.endedOn ? new Date(internship.endedOn) : null;
  if (endDate) {
    endDate.setHours(0, 0, 0, 0);
  }

  const isInProgress = internship.isActive && internship.startedOn && (!endDate || endDate > today);
  return isInProgress ? calculateMonthsBetween(internship.startedOn, today) : Number(internship.internshipCredits ?? 0);
}

export function formatFacilityTypeLabel(facilityType?: string | null): string {
  if (!facilityType) return "Unknown";
  const normalized = facilityType.toUpperCase();
  return (
    FACILITY_TYPE_LABELS[normalized] ??
    normalized
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char: string) => char.toUpperCase())
  );
}

export function buildInternshipReportingRows(
  internships: StudentInternshipsQuery | undefined,
  now: Date = new Date()
): InternshipReportingRow[] {
  const rowsByFacilityType = new Map<
    string,
    Omit<InternshipReportingRow, "studentCount" | "averageInternshipCredits"> & { studentIds: Set<string> }
  >();

  (internships?.studentInternships?.edges ?? []).forEach((edge) => {
    const internship = edge.node;
    const facilityType = formatFacilityTypeLabel(internship.facility?.facilityType);
    const currentRow = rowsByFacilityType.get(facilityType) ?? {
      id: facilityType,
      facilityType,
      internshipCount: 0,
      totalInternshipCredits: 0,
      studentIds: new Set<string>()
    };

    currentRow.internshipCount += 1;
    currentRow.totalInternshipCredits += getInternshipCredits(internship, now);

    if (internship.student?.pgaId) {
      currentRow.studentIds.add(internship.student.pgaId);
    }

    rowsByFacilityType.set(facilityType, currentRow);
  });

  return Array.from(rowsByFacilityType.values())
    .map(({ studentIds, ...row }) => ({
      ...row,
      studentCount: studentIds.size,
      totalInternshipCredits: roundToTenth(row.totalInternshipCredits),
      averageInternshipCredits: row.internshipCount ? roundToTenth(row.totalInternshipCredits / row.internshipCount) : 0
    }))
    .sort((a, b) => a.facilityType.localeCompare(b.facilityType));
}

export function buildInternshipReportingSummary(
  internships: StudentInternshipsQuery | undefined,
  now: Date = new Date()
): InternshipReportingSummary {
  const studentIds = new Set<string>();

  const totalInternshipCredits = (internships?.studentInternships?.edges ?? []).reduce((total, edge) => {
    const internship = edge.node;
    if (internship.student?.pgaId) {
      studentIds.add(internship.student.pgaId);
    }

    return total + getInternshipCredits(internship, now);
  }, 0);

  return {
    internshipCount: internships?.studentInternships?.totalCount ?? 0,
    studentCount: studentIds.size,
    totalInternshipCredits: roundToTenth(totalInternshipCredits)
  };
}

export function buildReportColumns(): ColumnDef<UniversityReportRow>[] {
  return [
    { accessorKey: "lastName", header: "Last", enableSorting: false, size: 120, minSize: 100 },
    { accessorKey: "firstName", header: "First", enableSorting: false, size: 120, minSize: 100 },
    { accessorKey: "studentId", header: "ID", enableSorting: false, size: 110, minSize: 90 },
    { accessorKey: "enrollment", header: "Enrollment", enableSorting: false, size: 120, minSize: 100 },
    {
      accessorKey: "programLevels",
      header: "Program Levels",
      enableSorting: false,
      size: 320,
      minSize: 260,
      cell: ({ row }) => <ProgramLevelsCell levels={row.original.programLevels} />
    },
    {
      accessorKey: "patAttempts12Months",
      header: "PAT 12 mo",
      enableSorting: false,
      size: 110,
      minSize: 90,
      meta: { className: "text-center" },
      cell: ({ row }) =>
        row.original.patAttempts12Months === "N/A" ? (
          <span className="opacity-20">--</span>
        ) : (
          row.original.patAttempts12Months
        )
    },
    {
      accessorKey: "pat",
      header: "PAT",
      enableSorting: false,
      size: 80,
      minSize: 70,
      meta: { className: "text-center" },
      cell: ({ row }) => <ReportBooleanCell value={row.original.pat} />
    },
    {
      accessorKey: "tournamentRequirements",
      header: "Tourn Req",
      enableSorting: false,
      size: 110,
      minSize: 90,
      meta: { className: "text-center" },
      cell: ({ row }) => <ReportBooleanCell value={row.original.tournamentRequirements} />
    },
    {
      accessorKey: "monthsEmployed",
      header: "Intern",
      enableSorting: false,
      size: 90,
      minSize: 70,
      meta: { className: "text-right tabular-nums" }
    },
    { accessorKey: "currentCohort", header: "Current Cohort", enableSorting: false, size: 140, minSize: 120 },
    { accessorKey: "originalCohort", header: "Original Cohort", enableSorting: false, size: 140, minSize: 120 },
    {
      accessorKey: "inOriginalCohort",
      header: "In Orig Cohort",
      enableSorting: false,
      size: 120,
      minSize: 100,
      meta: { className: "text-center" },
      cell: ({ row }) => <ReportBooleanCell value={row.original.inOriginalCohort} />
    },
    { accessorKey: "universityComments", header: "Comments", enableSorting: false, size: 150, minSize: 120 }
  ];
}

export function buildInternshipReportingColumns(): ColumnDef<InternshipReportingRow>[] {
  return [
    { accessorKey: "facilityType", header: "Facility Type", enableSorting: false, size: 280, minSize: 220 },
    { accessorKey: "internshipCount", header: "Internships", enableSorting: false, size: 120, minSize: 100 },
    { accessorKey: "studentCount", header: "Students", enableSorting: false, size: 120, minSize: 100 },
    {
      accessorKey: "totalInternshipCredits",
      header: "Total Credits",
      enableSorting: false,
      size: 140,
      minSize: 120,
      cell: ({ row }) => row.original.totalInternshipCredits.toFixed(1)
    },
    {
      accessorKey: "averageInternshipCredits",
      header: "Avg Credits",
      enableSorting: false,
      size: 140,
      minSize: 120,
      cell: ({ row }) => row.original.averageInternshipCredits.toFixed(1)
    }
  ];
}

export function formatRosterUploadProgramLevel(code?: string | null): string {
  if (!code) return "--";
  const version = LEVEL_CODE_TO_VERSION[code.charAt(0)];
  if (!version) return code;
  return PGM_VERSION_NAMES[version as keyof typeof PGM_VERSION_NAMES] || code;
}
